import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { ChatMessage, VideoInfo, SavedAnswer } from '../types/types';

interface BucketContextType {
  savedAnswers: SavedAnswer[];
  addToBucket: (message: ChatMessage, videoInfo: VideoInfo) => void;
  removeFromBucket: (id: string) => void;
  clearBucket: () => void;
  isInBucket: (id: string) => boolean;
  exportToPDF: () => void;
  exportToWord: () => void;
  copyToClipboard: () => Promise<boolean>;
  toggleDrawer: () => void;
  isDrawerOpen: boolean;
}

const BucketContext = createContext<BucketContextType | undefined>(undefined);

// Drawer state is managed here to allow multiple components to control it

interface BucketProviderProps {
  children: ReactNode;
}

export const BucketProvider: React.FC<BucketProviderProps> = ({ children }) => {
  const [savedAnswers, setSavedAnswers] = useState<SavedAnswer[]>(() => {
    try {
      const saved = localStorage.getItem('savedAnswers');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convert string dates back to Date objects
        return parsed.map((item: any) => ({
          ...item,
          savedAt: new Date(item.savedAt)
        }));
      }
      return [];
    } catch (error) {
      console.error('Error loading saved answers:', error);
      return [];
    }
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Save to localStorage whenever savedAnswers changes
  React.useEffect(() => {
    localStorage.setItem('savedAnswers', JSON.stringify(savedAnswers));
  }, [savedAnswers]);

  const addToBucket = useCallback((message: ChatMessage, videoInfo: VideoInfo) => {
    setSavedAnswers(prev => [
      ...prev,
      {
        id: `${message.id}-${Date.now()}`,
        message,
        videoInfo,
        savedAt: new Date()
      }
    ]);
  }, []);

  const removeFromBucket = useCallback((id: string) => {
    setSavedAnswers(prev => prev.filter(answer => answer.id !== id));
  }, []);

  const clearBucket = useCallback(() => {
    setSavedAnswers([]);
  }, []);

  const isInBucket = useCallback((id: string) => {
    return savedAnswers.some(answer => answer.id === id);
  }, [savedAnswers]);

  // Export functions
  const exportToPDF = useCallback(() => {
    // This will be implemented with a PDF library
    import('jspdf').then(({ default: jsPDF }) => {
      const doc = new jsPDF();
      
      let yPosition = 10;
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Group answers by video
      const answersByVideo: Record<string, SavedAnswer[]> = {};
      
      savedAnswers.forEach(answer => {
        const videoKey = answer.videoInfo.uuid || 'unknown';
        if (!answersByVideo[videoKey]) {
          answersByVideo[videoKey] = [];
        }
        answersByVideo[videoKey].push(answer);
      });
      
      // For each video, add video info and its answers
      Object.entries(answersByVideo).forEach(([_, answers], videoIndex) => {
        if (videoIndex > 0) {
          doc.addPage();
          yPosition = 10;
        }
        
        const videoInfo = answers[0].videoInfo;
        
        // Add video title
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(videoInfo.name, 10, yPosition);
        yPosition += 10;
        
        // Add video metadata
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        const creator = videoInfo.channel?.displayName || videoInfo.account?.displayName || 'Unbekannt';
        doc.text(`Ersteller: ${creator}`, 10, yPosition);
        yPosition += 5;
        
        if (videoInfo.views !== undefined) {
          doc.text(`Aufrufe: ${videoInfo.views}`, 10, yPosition);
          yPosition += 5;
        }
        
        if (videoInfo.createdAt) {
          doc.text(`Erstellt am: ${new Date(videoInfo.createdAt).toLocaleDateString()}`, 10, yPosition);
          yPosition += 5;
        }
        
        // Add video description if available
        if (videoInfo.description) {
          yPosition += 5;
          doc.setFontSize(12);
          doc.setFont('helvetica', 'italic');
          
          // Split description into lines that fit the page width
          const textLines = doc.splitTextToSize(videoInfo.description, pageWidth - 20);
          doc.text(textLines, 10, yPosition);
          yPosition += textLines.length * 5 + 10;
        } else {
          yPosition += 10;
        }
        
        // Add a divider
        doc.setDrawColor(200, 200, 200);
        doc.line(10, yPosition, pageWidth - 10, yPosition);
        yPosition += 10;
        
        // Add saved answers
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Gespeicherte Antworten:', 10, yPosition);
        yPosition += 10;
        
        answers.forEach((answer, index) => {
          doc.setFontSize(12);
          doc.setFont('helvetica', 'normal');
          
          // Format the message text
          const messageLines = doc.splitTextToSize(answer.message.text, pageWidth - 20);
          
          // Check if we need a new page
          if (yPosition + messageLines.length * 5 + 15 > doc.internal.pageSize.getHeight()) {
            doc.addPage();
            yPosition = 10;
          }
          
          // Add answer number and timestamp
          doc.setFont('helvetica', 'bold');
          doc.text(`Antwort ${index + 1} (${answer.savedAt.toLocaleString()})`, 10, yPosition);
          yPosition += 7;
          
          // Add answer text
          doc.setFont('helvetica', 'normal');
          doc.text(messageLines, 10, yPosition);
          yPosition += messageLines.length * 5 + 10;
        });
      });
      
      // Save the PDF
      doc.save('Gespeicherte_Antworten.pdf');
    });
  }, [savedAnswers]);

  const exportToWord = useCallback(() => {
    import('file-saver').then(({ default: FileSaver }) => {
      // Create a new document with HTML content
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Gespeicherte Antworten</title>
          <style>
            body { font-family: Arial, sans-serif; }
            .video-section { margin-bottom: 30px; }
            .video-title { font-size: 18px; font-weight: bold; }
            .video-meta { font-size: 12px; color: #666; margin-bottom: 10px; }
            .video-description { font-style: italic; margin-bottom: 15px; }
            .answers-section { margin-top: 15px; }
            .answer { margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #eee; }
            .answer-meta { font-size: 12px; color: #666; margin-bottom: 5px; }
            .answer-text { line-height: 1.5; }
          </style>
        </head>
        <body>
      `;

      // Group answers by video
      const answersByVideo: Record<string, SavedAnswer[]> = {};
      
      savedAnswers.forEach(answer => {
        const videoKey = answer.videoInfo.uuid || 'unknown';
        if (!answersByVideo[videoKey]) {
          answersByVideo[videoKey] = [];
        }
        answersByVideo[videoKey].push(answer);
      });

      // For each video, add video info and its answers
      Object.entries(answersByVideo).forEach(([_, answers]) => {
        const videoInfo = answers[0].videoInfo;
        
        htmlContent += `<div class="video-section">`;
        htmlContent += `<div class="video-title">${videoInfo.name}</div>`;
        
        const creator = videoInfo.channel?.displayName || videoInfo.account?.displayName || 'Unbekannt';
        htmlContent += `<div class="video-meta">`;
        htmlContent += `Ersteller: ${creator}`;
        
        if (videoInfo.views !== undefined) {
          htmlContent += ` | Aufrufe: ${videoInfo.views}`;
        }
        
        if (videoInfo.createdAt) {
          htmlContent += ` | Erstellt am: ${new Date(videoInfo.createdAt).toLocaleDateString()}`;
        }
        
        htmlContent += `</div>`;
        
        if (videoInfo.description) {
          htmlContent += `<div class="video-description">${videoInfo.description}</div>`;
        }
        
        htmlContent += `<div class="answers-section">`;
        htmlContent += `<h3>Gespeicherte Antworten:</h3>`;
        
        answers.forEach((answer, index) => {
          htmlContent += `<div class="answer">`;
          htmlContent += `<div class="answer-meta">Antwort ${index + 1} (${answer.savedAt.toLocaleString()})</div>`;
          htmlContent += `<div class="answer-text">${answer.message.text.replace(/\n/g, '<br>')}</div>`;
          htmlContent += `</div>`;
        });
        
        htmlContent += `</div></div>`;
      });

      htmlContent += `</body></html>`;
      
      // For Word documents, we'll use HTML format with the correct MIME type
      // This will open in Word when downloaded
      const blob = new Blob([htmlContent], { type: 'application/msword' });
      FileSaver.saveAs(blob, 'Gespeicherte_Antworten.doc');
    });
  }, [savedAnswers]);

  const copyToClipboard = useCallback(async (): Promise<boolean> => {
    try {
      // Create plain text of just the answers
      const textContent = savedAnswers.map(answer => 
        answer.message.text
      ).join('\n\n---\n\n');
      
      await navigator.clipboard.writeText(textContent);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  }, [savedAnswers]);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  return (
    <BucketContext.Provider value={{
      savedAnswers,
      addToBucket,
      removeFromBucket,
      clearBucket,
      isInBucket,
      exportToPDF,
      exportToWord,
      copyToClipboard,
      toggleDrawer,
      isDrawerOpen
    }}>
      {children}
    </BucketContext.Provider>
  );
};

export const useBucket = (): BucketContextType => {
  const context = useContext(BucketContext);
  if (context === undefined) {
    throw new Error('useBucket must be used within a BucketProvider');
  }
  return context;
};
